import { useState } from "react";
import { Slider, Input, Select, Button } from "antd";
import type { SelectProps } from "antd";
import {
  ModalOptionLabel,
  MinValueCheckbox,
  SortButtons,
  SortByButtons,
} from "./components";
import type { ColumnOption } from "@/components/VivoPlots/types";
import { MetricCardConfigTab } from "./MetricCardConfigTab";

export const ConfigTab = ({
  setters,
  getters,
  columnOptions,
  columnsTitleCreator,
}: {
  setters: any;

  getters: any;
  columnOptions: ColumnOption[];
  columnsTitleCreator: (column: string) => string;
}) => {
  const { plotType, maxBarsDisplayed } = getters;

  const [inputValue, setInputValue] = useState(
    maxBarsDisplayed === null ? "All" : maxBarsDisplayed.toString(),
  );

  if (plotType === "metric-card") {
    return (
      <MetricCardConfigTab
        setters={setters}
        getters={getters}
        columnOptions={columnOptions}
        columnsTitleCreator={columnsTitleCreator}
      />
    );
  }

  const {
    xVariable,
    xZeroAsMinValue,
    yVariable,
    yZeroAsMinValue,
    groupVariable,
    fillVariable,
    xSortBy,
    xSortDirection,
    isHorizontal,
    lineXIsDate,
  } = getters;

  const {
    setXVariable,
    setXZeroAsMinValue,
    setYVariable,
    setYZeroAsMinValue,
    setGroupVariable,
    setFillVariable,
    setXSortBy,
    setXSortDirection,
    setIsHorizontal,
    setMaxBarsDisplayed,
    setLineXIsDate,
  } = setters;

  const numericScatterColumns = columnOptions.filter((column) => {
    const dataType = column.data_type;
    if (!dataType) {
      return true;
    }
    return dataType === "numeric" || dataType === "datetime";
  });

  const axisColumnOptions =
    plotType === "scatter" ? numericScatterColumns : columnOptions;

  const isHorizontalBarplot = plotType === "barplot" && isHorizontal;
  const xLabel = isHorizontalBarplot ? "Define your Y" : "Define your X";
  const yLabel = isHorizontalBarplot ? "Define your X" : "Define your Y";
  const xPlaceholder = isHorizontalBarplot
    ? "Select Y Variable"
    : "Select X Variable";
  const yPlaceholder = isHorizontalBarplot
    ? "Select X Variable"
    : "Select Y Variable";
  const labelFilterOption: Exclude<SelectProps["filterOption"], boolean> = (
    input,
    option,
  ) =>
    String(option?.label ?? "")
      .toLowerCase()
      .includes(input.toLowerCase());

  return (
    <div className="flex flex-col gap-4 pr-2 pb-8">
      {plotType === "barplot" && (
        <div className="flex flex-col gap-2">
          <ModalOptionLabel label="Orientation" />
          <Select
            data-testid="plot-config-orientation-select"
            placeholder="Select bar plot orientation"
            className="w-full plotmodal"
            options={[
              {
                label: "Vertical",
                value: "vertical",
              },
              {
                label: "Horizontal",
                value: "horizontal",
              },
            ]}
            value={isHorizontal ? "horizontal" : "vertical"}
            onChange={(value) => setIsHorizontal(value === "horizontal")}
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <ModalOptionLabel label={xLabel} />
        <Select
          data-testid="plot-config-x-variable-select"
          placeholder={xPlaceholder}
          className="w-full plotmodal"
          showSearch
          filterOption={labelFilterOption}
          options={axisColumnOptions.map((column) => ({
            label: columnsTitleCreator(column.name),
            value: column.name,
          }))}
          value={xVariable}
          onChange={(e) => setXVariable(e)}
        />
        {plotType !== "scatter" && (
          <SortButtons xSort={xSortDirection} setXSort={setXSortDirection} />
        )}
        {plotType === "barplot" && xSortDirection !== "none" && (
          <SortByButtons xSortBy={xSortBy} setXSortBy={setXSortBy} />
        )}

        {plotType === "barplot" && (
          <div className="mt-2">
            <div className="flex items-center justify-between gap-2 mb-2">
              <ModalOptionLabel
                label="Max bars displayed"
                tooltip="The maximum number of bars you can display"
              />
              <div className="flex items-center gap-1">
                <Input
                  data-testid="plot-config-max-bars-input"
                  value={inputValue}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setInputValue(newValue);
                    const numValue = Number.parseInt(newValue);
                    if (
                      !Number.isNaN(numValue) &&
                      numValue >= 1 &&
                      numValue <= 50
                    ) {
                      setMaxBarsDisplayed(numValue);
                    }
                  }}
                  onBlur={(e) => {
                    const numValue = Number.parseInt(e.target.value);
                    if (
                      Number.isNaN(numValue) ||
                      numValue < 1 ||
                      numValue > 50
                    ) {
                      setInputValue(
                        maxBarsDisplayed === null
                          ? "All"
                          : maxBarsDisplayed.toString(),
                      );
                    }
                  }}
                  className="w-12"
                />
                <Button
                  data-testid="plot-config-max-bars-all-button"
                  size="small"
                  type="default"
                  onClick={() => {
                    setMaxBarsDisplayed(null);
                    setInputValue("All");
                  }}
                  className="px-[8px] py-[4px] h-auto text-[#2B4752]"
                >
                  All
                </Button>
              </div>
            </div>
            <div className="relative">
              <Slider
                min={1}
                max={50}
                value={maxBarsDisplayed === null ? 50 : maxBarsDisplayed}
                onChange={(value: number) => {
                  setMaxBarsDisplayed(value);
                  setInputValue(value.toString());
                }}
                className={maxBarsDisplayed === null ? "opacity-40" : ""}
              />
              {maxBarsDisplayed === null && (
                <div className="text-xs text-gray-500 mt-1">
                  Showing all bars - move slider to set limit
                </div>
              )}
            </div>
          </div>
        )}

        {plotType === "scatter" && (
          <MinValueCheckbox
            zeroAsMinValue={xZeroAsMinValue}
            setZeroAsMinValue={setXZeroAsMinValue}
          />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <ModalOptionLabel label={yLabel} />
        <Select
          data-testid="plot-config-y-variable-select"
          placeholder={yPlaceholder}
          disabled={plotType === "barplot"}
          className="w-full plotmodal"
          showSearch
          filterOption={labelFilterOption}
          options={axisColumnOptions.map((column) => ({
            label: columnsTitleCreator(column.name),
            value: column.name,
          }))}
          value={yVariable}
          onChange={(e) => setYVariable(e)}
        />
        {plotType !== "barplot" && (
          <MinValueCheckbox
            zeroAsMinValue={yZeroAsMinValue}
            setZeroAsMinValue={setYZeroAsMinValue}
          />
        )}
      </div>
      {plotType !== "scatter" && (
        <div className="flex flex-col gap-2">
          <ModalOptionLabel
            label="Group by"
            tooltip="Select a variable by which to group the data."
          />
          <Select
            data-testid="plot-config-group-variable-select"
            placeholder="Enter Group Variable"
            defaultValue="none"
            className="w-full plotmodal"
            showSearch
            filterOption={labelFilterOption}
            options={[
              {
                label: "None",
                value: "none",
              },
              ...columnOptions.map((column) => ({
                label: columnsTitleCreator(column.name),
                value: column.name,
              })),
            ]}
            value={groupVariable}
            onChange={(e) => setGroupVariable(e)}
          />
        </div>
      )}
      {plotType === "line" && (
        <div className="flex flex-col gap-2">
          <ModalOptionLabel
            label="X axis type"
            tooltip="Treat the X column as time (a date, or a YYYYMM month like month_id) for correct chronological ordering and tick formatting, or as a plain number."
          />
          <Select
            data-testid="plot-config-line-x-axis-type-select"
            className="w-full plotmodal"
            value={lineXIsDate ? "date" : "numeric"}
            options={[
              { label: "Date / month", value: "date" },
              { label: "Numeric", value: "numeric" },
            ]}
            onChange={(v) => setLineXIsDate(v === "date")}
          />
        </div>
      )}
      <div className="flex flex-col gap-2">
        <ModalOptionLabel
          label="Color By"
          tooltip="Select a variable by which to color the data."
        />
        <Select
          data-testid="plot-config-fill-variable-select"
          placeholder="Enter Fill Variable"
          className="w-full plotmodal"
          defaultValue="none"
          showSearch
          filterOption={labelFilterOption}
          options={[
            {
              label: "None",
              value: "none",
            },
            ...columnOptions.map((column) => ({
              label: columnsTitleCreator(column.name),
              value: column.name,
            })),
          ]}
          value={fillVariable}
          onChange={(e) => setFillVariable(e)}
        />
      </div>
    </div>
  );
};
